import React from 'react';
import {
  Modal,
  Text,
  Group,
  Button,
  Progress,
  Box,
  Loader,
  Alert,
  Card,
  ActionIcon,
  Divider,
} from '@mantine/core';
import { IconCheck, IconAlertCircle, IconX, IconRefresh } from '@tabler/icons';

interface ProcessItemsModalProps {
  opened: boolean;
  onClose: () => void;
  processed: number;
  unprocessed: number;
  isProcessing: boolean;
  onProcess: () => void;
  error?: string | null;
}

export const ProcessItemsModal: React.FC<ProcessItemsModalProps> = ({
  opened,
  onClose,
  processed,
  unprocessed,
  isProcessing,
  onProcess,
  error,
}) => {
  const total = processed + unprocessed;
  const percent = total > 0 ? (processed / total) * 100 : 0;

  const showNoItems = processed === 0 && unprocessed === 0;

  return (
    <Modal opened={opened} onClose={onClose} centered padding={0} withCloseButton={false}>
      <Group p="xs" position="apart">
        <Group spacing={8}>
          <IconRefresh size={18} stroke={1.5} />
          <Text fw={500}>Reprocess Items</Text>
        </Group>

        <ActionIcon onClick={onClose}>
          <IconX size={16} />
        </ActionIcon>
      </Group>
      <Divider />
      <Box p="sm">
        <Box>
          {showNoItems ? (
            <Card bg="gray.1" p="md">
              <Text align="center" color="dimmed">
                No items to process.
              </Text>
              <Text align="center" color="dimmed">
                Run a pre-process sync first.
              </Text>
            </Card>
          ) : (
            <>
              <Group position="apart">
                <Text>
                  Processed: <b>{processed.toLocaleString()}</b>
                </Text>
                <Text color={unprocessed > 0 ? 'red' : 'green'}>
                  Unprocessed: <b>{unprocessed.toLocaleString()}</b>
                </Text>
              </Group>
              <Progress
                value={percent}
                mt="md"
                color={percent < 40 ? 'red' : percent < 70 ? 'orange' : 'green'}
                animate={percent !== 100}
                striped
              />
              <Group position="apart" mt="xs">
                <Text size="xs" color="dimmed">
                  {percent.toFixed(1)}% processed
                </Text>
                {unprocessed === 0 && (
                  <Group spacing={4}>
                    <IconCheck size={16} color="green" />
                    <Text size="xs" color="green">
                      All items processed
                    </Text>
                  </Group>
                )}
              </Group>
            </>
          )}
        </Box>

        {!showNoItems && (
          <Button
            mt="md"
            fullWidth
            color="teal"
            leftIcon={isProcessing ? <Loader size={16} /> : <IconRefresh size={16} />}
            onClick={onProcess}
            loading={isProcessing}
            disabled={isProcessing}
          >
            Reprocess {unprocessed.toLocaleString()} item{unprocessed !== 1 ? 's' : ''}
          </Button>
        )}

        {error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />} mt="md">
            {error}
          </Alert>
        )}
      </Box>
    </Modal>
  );
};

export default ProcessItemsModal;
